import { MfaPlatform } from 'constants/MfaPlatform';
import { MfaRole } from 'constants/MfaRole';
import { IAgencyMfaCell, IAgencyMfaPolicy } from 'types/mfa/IAgencyMfaPolicy';
import dayjs from 'utils/dayjs';

export const API_DATE_FORMAT = 'YYYY-MM-DD';

/**
 * `utils/dayjs` does NOT extend customParseFormat, so dayjs(value, API_DATE_FORMAT) would misparse.
 * The API's value is already ISO, which dayjs parses natively — so parse it without a format.
 */
export const toDateOrNull = (value: string | null): Date | null => (value ? dayjs(value).toDate() : null);

export const toApiDate = (value: Date | null): string | null => (value ? dayjs(value).format(API_DATE_FORMAT) : null);

export const findCell = (cells: IAgencyMfaCell[], role: MfaRole, platform: MfaPlatform): IAgencyMfaCell | undefined =>
  cells.find(cell => cell.role === role && cell.platform === platform);

/** Returns a new array — the six cells keep their order, which the API requires. */
export const setCell = (
  cells: IAgencyMfaCell[],
  role: MfaRole,
  platform: MfaPlatform,
  isRequired: boolean,
): IAgencyMfaCell[] =>
  cells.map(cell => (cell.role === role && cell.platform === platform ? { ...cell, isRequired } : cell));

/**
 * True when the date has already passed.
 *
 * A stored date that merely aged past today is the grace period having *ended* — a legitimate
 * state, not a validation failure. US 590 rejects only a past date the SuperAdmin newly enters,
 * so callers must combine this with a dirty check before treating it as an error.
 */
export const hasGracePeriodElapsed = (value: string | null): boolean =>
  value !== null && dayjs(value).isBefore(dayjs(), 'day');

export const isMatrixDirty = (draft: IAgencyMfaPolicy, saved: IAgencyMfaPolicy): boolean =>
  draft.cells.some(cell => findCell(saved.cells, cell.role, cell.platform)?.isRequired !== cell.isRequired);

export const isDateDirty = (draft: IAgencyMfaPolicy, saved: IAgencyMfaPolicy): boolean =>
  draft.mandatoryActivationDate !== saved.mandatoryActivationDate;

export const isPolicyDirty = (draft: IAgencyMfaPolicy, saved: IAgencyMfaPolicy): boolean =>
  isMatrixDirty(draft, saved) || isDateDirty(draft, saved);

export enum MfaConfirmation {
  /** Enforcement only decreases, or nothing enforcement-relevant changed. */
  NONE = 'none',
  /** A cell was turned on and a future activation date governs when it bites. */
  ENFORCEMENT_INCREASED = 'enforcement-increased',
  /** Users are affected at their next login, with no grace period. */
  ENFORCEMENT_IMMEDIATE = 'enforcement-immediate',
}

/**
 * Decides what the SuperAdmin has to confirm before a save.
 *
 * Turning cells OFF never prompts — it only reduces enforcement. The two prompting cases are:
 *  - the activation date is cleared, which the API treats as "mandatory immediately", not "never";
 *  - a cell is turned on while the effective date is absent or already past, so affected users are
 *    challenged at their very next login.
 */
export const getRequiredConfirmation = (draft: IAgencyMfaPolicy, saved: IAgencyMfaPolicy): MfaConfirmation => {
  const anyCellTurnedOn = draft.cells.some(
    cell => cell.isRequired && !findCell(saved.cells, cell.role, cell.platform)?.isRequired,
  );
  const anyCellRequired = draft.cells.some(cell => cell.isRequired);
  const dateCleared = anyCellRequired && isDateDirty(draft, saved) && draft.mandatoryActivationDate === null;
  const effectiveDateIsImmediate =
    !draft.mandatoryActivationDate || hasGracePeriodElapsed(draft.mandatoryActivationDate);

  if (dateCleared || (anyCellTurnedOn && effectiveDateIsImmediate)) {
    return MfaConfirmation.ENFORCEMENT_IMMEDIATE;
  }
  if (anyCellTurnedOn) {
    return MfaConfirmation.ENFORCEMENT_INCREASED;
  }
  return MfaConfirmation.NONE;
};
